import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FormSpy } from 'react-final-form';

import { lodashGet } from 'functions/lodashGet';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getActEventInspectorsBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import { CloseButton } from '../../../UIPackage/MainModule/components/CloseButton';
import { createKnmEventInspectorSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnmEventInspectorSelectOption';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';
import { IKnmErknmFormEventInspector } from '../../MainModule/interfaces/IKnmErknmFormEventInspector';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IKnmErknmForm } from '../../MainModule/interfaces/IKnmErknmForm';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import styles from './ErknmEventInspectors.module.scss';

interface IErknmEventInspectorsProps {
  eventId: string;
  lateFieldName?: string;
  name: string;
  watchOnly: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  value?: IKnmErknmFormAct | IKnmPmFormAct | IDecisionActForm;
  actLevelName?: string;
}

interface IErknmEventInspectorsStoreProps {
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
}

const ErknmEventInspectorsWithoutStore = ({
  eventId,
  lateFieldName,
  name,
  watchOnly,
  formRef,
  isPm,
  isDecision,
  value,
  actLevelName,
}: IErknmEventInspectorsProps & IErknmEventInspectorsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const [inspectorOptions, setInspectorOptions] = useState<ISelectOption[]>([]);

  const inspectorsFieldName = isDecision ? 'inspectors' : 'inspectorsErknm';

  const setInspectorsOptions = () => {
    // @ts-ignore
    const eventInspectors: IKnmErknmFormEventInspector[] | undefined = formRef?.current.getFieldState(
      `${name}` as keyof IKnmErknmForm,
    )?.value;
    const options: ISelectOption[] = [];

    // @ts-ignore
    formRef?.current.getState().values?.[inspectorsFieldName]?.forEach(x => {
      if (x.fullName && x.listId) {
        const option = createKnmEventInspectorSelectOption(`${x.fullName} - ${x.position?.title}`, x.listId);

        if (!eventInspectors) {
          options.push(option);
        } else if (!eventInspectors.some(i => i.fullName?.value.includes(x.fullName))) {
          options.push(option);
        }
      }
    });

    return options;
  };

  const getInspectorsOptions = useCallback(
    (selectedOptions: IKnmErknmFormEventInspector[]) => {
      return inspectorOptions.filter(i => !selectedOptions.find(j => j.fullName?.id === i.id));
    },
    [inspectorOptions],
  );

  const addEventInspector = useCallback(() => {
    if (!watchOnly && push) {
      const addingEventInspector: IKnmErknmFormEventInspector = {
        guid: uuidv4(),
        fullName: undefined,
      };

      setInspectorOptions(setInspectorsOptions());

      push(`${name}`, addingEventInspector);
    }
  }, [push, watchOnly]);

  const makeRemoveInspectorHandler = (index: number) => () => {
    !watchOnly && remove && remove(`${name}`, index);
    setInspectorOptions(setInspectorsOptions());
  };

  const handleInspectorChange = useCallback(
    (v: ISelectOption) => {
      setInspectorOptions(setInspectorsOptions());

      if (v) {
        // @ts-ignore
        const inspector = formRef?.current.getState().values?.[inspectorsFieldName]?.find(i => i.listId === v.id);
        const obj = {
          id: inspector?.guid || '',
          value: inspector,
          label: `${inspector?.fullName} - ${inspector?.position?.label}`,
        };

        if (value?.knoInspectors) {
          if (
            !value.knoInspectors.find(
              inspec =>
                inspec.knoInspector?.id === v.id ||
                inspec.knoInspector?.value?.listId === v.value.listId ||
                inspec.knoInspector?.value.fullName === v.value.fullName,
            )
          ) {
            // @ts-ignore
            formRef?.current?.change(`${actLevelName}.knoInspectors`, [...value.knoInspectors, { knoInspector: obj }]);
          }
        } else {
          // @ts-ignore
          formRef?.current?.change(`${actLevelName}.knoInspectors`, [{ knoInspector: obj }]);
        }
      }
    },
    [value, formRef, actLevelName],
  );

  return (
    <FieldBlock
      className={styles.GroupMargin}
      label={getTitleFieldWithRedStar({
        label: 'Должностные лица, осуществившие действие',
        field: name,
        isPm,
        isDecision,
        isKnm: !isPm && !isDecision,
      })}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          id="erknmEventInspectorsAddButton"
          disabled={watchOnly}
          onClick={addEventInspector}
        />
      }
      labelId={`${eventId}.inspectors`}
      tooltip={
        <KnmLateFieldsRemark
          lateFieldPath={`${lateFieldName}/inspectors`}
          fieldId={`${eventId}.inspectors`}
          isLeftPosition={true}
        />
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = lodashGet(props.errors || {}, name);
          const isTouched = props.touched?.[name];

          if (isTouched && fieldError && fieldError['FINAL_FORM/array-error']) {
            return (
              <p className={`shared-input-error shared-row__small-left-margin ${styles.ErrorMsgWrapper}`}>
                {fieldError['FINAL_FORM/array-error']}
              </p>
            );
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={name} validate={makeRequired(REQUIRED_ERROR)}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormEventInspector, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <>
              {fields.map((inspectorFieldName, sIndex) => {
                const fieldValue: IKnmErknmFormEventInspector = fields.value?.[sIndex];
                const id = getActEventInspectorsBlockId(eventId, fieldValue.guid);

                return (
                  <Row key={id} id={id} notWrap>
                    <FieldBlock id={`${id}.fullName`}>
                      <SelectInputField
                        name={`${inspectorFieldName}.fullName`}
                        placeholder={getTitleFieldWithRedStar({
                          label: 'Выберите должностное лицо, осуществившее действие',
                          field: `${inspectorFieldName}.fullName`,
                          isPm,
                          isDecision,
                          isKnm: !isPm && !isDecision,
                        })}
                        options={getInspectorsOptions(fields.value)}
                        disabled={watchOnly}
                        isClearable
                        isSearchable
                        size="large"
                        onChange={handleInspectorChange}
                      />
                    </FieldBlock>
                    {!watchOnly && (
                      <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveInspectorHandler(sIndex)} />
                    )}
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/inspectors/${fieldValue.guid}/fullName`}
                      fieldId={`${id}.fullName`}
                      isLeftPosition={true}
                    />
                  </Row>
                );
              })}
            </>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState, ownProps: IErknmEventInspectorsProps): IErknmEventInspectorsStoreProps => ({
  formRef: ownProps?.isDecision ? getDecisionFormRef(state) : getKnmFormRef(state),
});

export const ErknmEventInspectors = connect(mapStateToProps)(ErknmEventInspectorsWithoutStore);
